import React, { useState, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import api from "api";
import PageHeader from "components/layout/page-header/PageHeader";
import styles from "./AdminNotifications.module.css";
import AppModal from "components/ui/AppModal/AppModal";

const TITLE_MAX = 80;
const BODY_MAX  = 300;

function AdminNotifications({ token }) {
  const [users, setUsers]           = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [sendTarget, setSendTarget] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [title, setTitle]           = useState("");
  const [body, setBody]             = useState("");
  const [sending, setSending]       = useState(false);

  const [modal, setModal] = useState({ open: false, variant: "custom", title: "", message: "", onConfirm: null });
  const modalResolveRef = useRef(null);

  const openModal = (config) =>
    new Promise(resolve => {
      modalResolveRef.current = resolve;
      setModal({ ...config, open: true });
    });

  const closeModal = () => {
    setModal(m => ({ ...m, open: false }));
    modalResolveRef.current?.();
    modalResolveRef.current = null;
  };

  useEffect(() => {
    if (!token) return;
    setLoadingUsers(true);
    api.get("/users/all", { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false));
  }, [token]);

  const handleSelectAll = () => {
    setSelectedIds(
      selectedIds.length === users.length ? [] : users.map(u => u.id)
    );
  };

  const userOptions = useMemo(
    () => users.map(u => ({
      value: u.id,
      label: `${u.name || u.username} (${u.email})`
    })),
    [users]
  );

  const selectedOptions = useMemo(
    () => userOptions.filter(opt => selectedIds.includes(opt.value)),
    [userOptions, selectedIds]
  );

  const handleSubmit = async e => {
    e.preventDefault();
    if (!title.trim()) {
      await openModal({ variant: "warning", title: "Atenção", message: "Preencha o título.", onConfirm: closeModal });
      return;
    }
    if (!body.trim()) {
      await openModal({ variant: "warning", title: "Atenção", message: "Preencha a mensagem.", onConfirm: closeModal });
      return;
    }
    if (sendTarget === "selected" && selectedIds.length === 0) {
      await openModal({ variant: "warning", title: "Atenção", message: "Selecione ao menos um destinatário.", onConfirm: closeModal });
      return;
    }
    setSending(true);
    try {
      const payload = {
        title: title.trim(),
        body: body.trim(),
        user_ids: sendTarget === "all" ? null : selectedIds
      };
      const { data } = await api.post("/admin/notifications/send", payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await openModal({
        variant: "success",
        title: "Enviado!",
        message: `Notificação enviada para ${data.recipients_count} destinatário(s).`,
        onConfirm: closeModal,
      });
      setTitle("");
      setBody("");
      setSelectedIds([]);
    } catch (err) {
      await openModal({
        variant: "error",
        title: "Erro",
        message: err.response?.data?.detail || "Falha ao enviar notificação.",
        onConfirm: closeModal,
      });
    } finally {
      setSending(false);
    }
  };

  /* Character count helpers */
  const titlePct = title.length / TITLE_MAX;
  const bodyPct  = body.length / BODY_MAX;
  const countClass = pct =>
    pct >= 1 ? styles.charCountOver : pct >= 0.85 ? styles.charCountWarn : styles.charCount;

  const previewTitle = title || "Título da notificação";
  const previewBody  = body  || "Prévia da mensagem que os usuários receberão.";

  return (
    <div className={styles.pageContainer}>
      <PageHeader
        title="Enviar notificação"
        subtitle="Crie e envie uma notificação para usuários selecionados ou para todos."
        accent="Administração"
      />

      <div className={styles.content}>

        {/* ── Compose card ── */}
        <form onSubmit={handleSubmit} className={styles.card} noValidate>
          <p className={styles.cardTitle}>Nova notificação</p>

          <div className={styles.formBody}>

            {/* Título */}
            <div className={styles.formGroup}>
              <div className={styles.formLabelRow}>
                <label htmlFor="notif-title" className={styles.formLabel}>Título</label>
                <span className={countClass(titlePct)}>
                  {title.length}/{TITLE_MAX}
                </span>
              </div>
              <input
                id="notif-title"
                type="text"
                className={styles.formControl}
                placeholder="Ex.: Manutenção programada"
                value={title}
                onChange={e => setTitle(e.target.value.slice(0, TITLE_MAX))}
                autoComplete="off"
              />
            </div>

            {/* Mensagem */}
            <div className={styles.formGroup}>
              <div className={styles.formLabelRow}>
                <label htmlFor="notif-body" className={styles.formLabel}>Mensagem</label>
                <span className={countClass(bodyPct)}>
                  {body.length}/{BODY_MAX}
                </span>
              </div>
              <textarea
                id="notif-body"
                className={styles.formControl}
                rows={5}
                placeholder="Texto da notificação que os usuários verão..."
                value={body}
                onChange={e => setBody(e.target.value.slice(0, BODY_MAX))}
              />
            </div>

            {/* Destinatários */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Destinatários</label>
              <div className={styles.modeToggle}>
                <button
                  type="button"
                  className={`${styles.modeBtn} ${sendTarget === "all" ? styles.modeBtnActive : ""}`}
                  onClick={() => setSendTarget("all")}
                >
                  <i className="fas fa-users" />
                  Todos os usuários
                </button>
                <button
                  type="button"
                  className={`${styles.modeBtn} ${sendTarget === "selected" ? styles.modeBtnActive : ""}`}
                  onClick={() => setSendTarget("selected")}
                >
                  <i className="fas fa-user-check" />
                  Selecionar usuários
                </button>
              </div>

              {sendTarget === "selected" && (
                <div className={styles.usersBox}>
                  <span className={styles.usersBoxLabel}>Buscar usuários</span>
                  {loadingUsers ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div className={styles.spinner} />
                      <span style={{ fontSize: "0.82rem", color: "#94a3b8" }}>
                        Carregando usuários...
                      </span>
                    </div>
                  ) : (
                    <>
                      <Select
                        isMulti
                        isSearchable
                        options={userOptions}
                        value={selectedOptions}
                        onChange={selected =>
                          setSelectedIds((selected || []).map(s => s.value))
                        }
                        placeholder="Digite para buscar por nome ou e-mail..."
                        noOptionsMessage={() => "Nenhum usuário encontrado"}
                        className="admin-notif-react-select"
                        classNamePrefix="admin-notif-select"
                      />
                      <div className={styles.selectActions}>
                        <button
                          type="button"
                          className={styles.selectAllBtn}
                          onClick={handleSelectAll}
                        >
                          <i
                            className={`fas ${
                              selectedIds.length === users.length && users.length > 0
                                ? "fa-times-circle"
                                : "fa-check-circle"
                            }`}
                            style={{ marginRight: 4 }}
                          />
                          {selectedIds.length === users.length && users.length > 0
                            ? "Desmarcar todos"
                            : `Selecionar todos (${users.length})`}
                        </button>
                        {selectedIds.length > 0 && (
                          <span className={styles.selectedCount}>
                            {selectedIds.length} selecionado(s)
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className={styles.formActions}>
            <button
              type="submit"
              className={`${styles.button} ${styles.buttonPrimary}`}
              disabled={sending}
            >
              {sending ? (
                <>
                  <div className={styles.spinner} />
                  Enviando...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane" />
                  Enviar notificação
                </>
              )}
            </button>
            {sendTarget === "all" && !sending && (
              <span style={{ fontSize: "0.78rem", color: "#94a3b8" }}>
                <i className="fas fa-info-circle" style={{ marginRight: 4 }} />
                {users.length > 0 ? `${users.length} usuário(s)` : "Todos os usuários"}
              </span>
            )}
          </div>
        </form>

        {/* ── Preview panel ── */}
        <div className={styles.previewCard}>
          <p className={styles.previewTitle}>Prévia</p>
          <div className={styles.phoneMock}>
            <div className={styles.phoneMockBar}>
              <span className={styles.phoneMockTime}>9:41</span>
              <div className={styles.phoneMockDots}>
                <div className={styles.phoneMockDot} />
                <div className={styles.phoneMockDot} />
                <div className={styles.phoneMockDot} />
              </div>
            </div>
            <div className={styles.notifBubble}>
              <div className={styles.notifIcon}>
                <i className="fas fa-satellite-dish" />
              </div>
              <div className={styles.notifContent}>
                <div className={styles.notifAppName}>Vertex 4D</div>
                <div
                  className={`${styles.notifHeadline} ${!title ? styles.notifEmpty : ""}`}
                >
                  {previewTitle}
                </div>
                <div
                  className={`${styles.notifBody} ${!body ? styles.notifEmpty : ""}`}
                >
                  {previewBody}
                </div>
                <div className={styles.notifTime}>agora</div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ── Modal unificado ───────────────────────────────────────────── */}
      <AppModal
        isOpen={modal.open}
        onClose={closeModal}
        allowOutsideClick={true}
        variant={modal.variant}
        title={modal.title}
        message={modal.message}
        confirmText="OK"
        onConfirm={modal.onConfirm}
      />
    </div>
  );
}

const mapStateToProps = state => ({
  token: state.auth?.accessToken ?? null
});

export default connect(mapStateToProps)(AdminNotifications);
